import { useState } from "react";
import { Button } from "../../ui/button";
import { Notice } from "../../ui/notice";
import { SectionCard } from "../../ui/section-card";
import { StatusBadge } from "../../ui/status-badge";
import { StatusMessage } from "../../ui/status-message";
import { type AllowedAction, allowedActions, type TransitionAction } from "../allowed-actions";
import { describeOutcome, type SaveMessage } from "../commands";
import { ItemStatusBadge } from "../components/ItemStatusBadge";
import { ReasonDialog } from "../components/ReasonDialog";
import { hintText, subheading } from "../components/styles";
import { itemStatusLabels, publishabilityIssueInfo, type SectionId } from "../labels";
import { derivedWarnings, splitIssues } from "../publishability";
import { useAnyDirty } from "../unsaved";
import type { ItemSectionContext } from "./section-support";

const ISSUES_ID = "readiness-issues";

const issueRow = "flex flex-wrap items-center gap-x-2 gap-y-0 leading-6";

const successText: Readonly<Record<TransitionAction, string>> = {
  submitForReview: "Submitted for review.",
  returnToDraft: "Returned to draft.",
  publish: "Published. Customers can see this item now.",
  unpublish: "Unpublished. Customers no longer see this item.",
  archive: "Archived.",
};

function SectionLink({ section, children }: Readonly<{ section: SectionId; children: string }>) {
  return (
    <a
      href={`#${section}`}
      className="inline-flex min-h-11 items-center text-link underline underline-offset-4 hover:no-underline compact:min-h-8"
      onClick={(event) => {
        event.preventDefault();
        const target = document.getElementById(section);
        target?.scrollIntoView?.({ block: "start" });
        target?.querySelector<HTMLElement>("input, select, textarea, button")?.focus();
      }}
    >
      {children}
    </a>
  );
}

/**
 * Readiness before publish, and the status controls. The readiness list is the server's own answer for
 * the saved item: BLOCKING issues disable Publish, WARNING issues and the console's derived warnings
 * are shown but never block. Each status change is a domain command with a version token.
 */
export function PublishPanel({ ctx }: Readonly<{ ctx: ItemSectionContext }>) {
  const { detail, command, capabilities, now } = ctx;
  const { item } = detail;
  const anyDirty = useAnyDirty();
  const [message, setMessage] = useState<SaveMessage | null>(null);
  const [asking, setAsking] = useState<AllowedAction | null>(null);

  const { blocking, warnings } = splitIssues(detail.publishability.issues);
  const extra = derivedWarnings(detail, now);
  const actions = allowedActions(item.status, capabilities);

  async function run(action: AllowedAction, reason?: string) {
    const input =
      reason === undefined
        ? { expectedVersion: item.version }
        : { expectedVersion: item.version, reason };
    const outcome = await command.run(action.action, input as never);
    return outcome;
  }

  async function perform(action: AllowedAction) {
    if (action.needsReason) {
      setAsking(action);
      return;
    }
    setMessage(null);
    const outcome = await run(action);
    setMessage(describeOutcome(outcome, successText[action.action]));
  }

  async function confirm(action: AllowedAction, reason: string): Promise<string | null> {
    const outcome = await run(action, reason);
    if (outcome.ok) {
      setMessage(describeOutcome(outcome, successText[action.action]));
      return null;
    }
    return describeOutcome(outcome, "").text;
  }

  const publishBlocked = blocking.length > 0;

  return (
    <SectionCard
      id="publishing"
      title="Status and publishing"
      className="border-2 border-primary"
      description={
        <>
          Now: <ItemStatusBadge status={item.status} />
          {item.status === "PUBLISHED" ? " Customers can see this item." : null}
        </>
      }
    >
      <div className="grid gap-2" aria-live="polite">
        <h3 className={subheading}>Readiness</h3>
        {blocking.length === 0 && warnings.length === 0 && extra.length === 0 ? (
          <Notice tone="success">No problems found in the saved item.</Notice>
        ) : null}
        {blocking.length > 0 ? (
          <div className="grid gap-2">
            <p className="m-0 font-semibold">
              {blocking.length} {blocking.length === 1 ? "problem stops" : "problems stop"}{" "}
              publishing
            </p>
            <ul id={ISSUES_ID} className="m-0 grid list-none gap-1 p-0">
              {blocking.map((issue) => {
                const info = publishabilityIssueInfo[issue.code];
                return (
                  <li key={issue.code} className={issueRow}>
                    <StatusBadge tone="danger">Blocking</StatusBadge>
                    <span>{info.message}</span>
                    <SectionLink section={info.section}>Go to the field</SectionLink>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}
        {warnings.length > 0 || extra.length > 0 ? (
          <div className="grid gap-2">
            <p className="m-0 font-semibold">Worth a look</p>
            <ul className="m-0 grid list-none gap-1 p-0">
              {warnings.map((issue) => {
                const info = publishabilityIssueInfo[issue.code];
                return (
                  <li key={issue.code} className={issueRow}>
                    <StatusBadge tone="warning">Warning</StatusBadge>
                    <span>{info.message}</span>
                    <SectionLink section={info.section}>Go to the field</SectionLink>
                  </li>
                );
              })}
              {extra.map((warning) => (
                <li key={warning.id} className={issueRow}>
                  <StatusBadge tone="warning">Warning</StatusBadge>
                  <span>{warning.message}</span>
                  <SectionLink section={warning.section}>Go to the field</SectionLink>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>

      {item.status === "ARCHIVED" ? (
        <Notice>An archived item cannot be changed or published again.</Notice>
      ) : null}

      {actions.length > 0 ? (
        <>
          {anyDirty ? (
            <Notice tone="warning" role="status">
              You have unsaved changes. Save or discard them first: these actions use the saved
              version of the item.
            </Notice>
          ) : null}
          <fieldset className="m-0 flex min-w-0 flex-wrap gap-2 border-0 p-0">
            <legend className="sr-only">Status actions</legend>
            {actions.map((action) => {
              const disabled =
                command.pending || anyDirty || (action.needsPublishable && publishBlocked);
              return (
                <Button
                  key={action.action}
                  type="button"
                  variant={
                    action.tone === "danger"
                      ? "destructive"
                      : action.tone === "neutral"
                        ? "secondary"
                        : "default"
                  }
                  disabled={disabled}
                  aria-describedby={
                    action.needsPublishable && publishBlocked ? ISSUES_ID : undefined
                  }
                  onClick={() => void perform(action)}
                >
                  {action.label}
                </Button>
              );
            })}
          </fieldset>
          {actions.some((action) => action.needsPublishable) && publishBlocked ? (
            <p className={hintText}>
              Publish is off until the {blocking.length === 1 ? "problem" : "problems"} above{" "}
              {blocking.length === 1 ? "is" : "are"} fixed.
            </p>
          ) : null}
        </>
      ) : item.status !== "ARCHIVED" ? (
        <p className={hintText}>
          Your role cannot change the status of this item ({itemStatusLabels[item.status]}).
        </p>
      ) : null}

      <StatusMessage message={message} />

      {asking !== null ? (
        <ReasonDialog
          title={`${asking.label} this item`}
          description={
            asking.action === "unpublish"
              ? "Customers will stop seeing this item at once."
              : "An archived item cannot be changed or published again."
          }
          confirmLabel={asking.label}
          tone={asking.tone === "danger" ? "danger" : "primary"}
          onConfirm={(reason) => confirm(asking, reason)}
          onClose={() => setAsking(null)}
        />
      ) : null}
    </SectionCard>
  );
}
